'use client';

/**
 * Session history panel — a slide-over (Sheet) that lists the conversation as
 * TURNS (newest first) with revert, bookmarks, and "undo last edit". Built on
 * the existing branch mechanism: "Revert to here" forks a NON-DESTRUCTIVE
 * branch at a turn (never deletes the original), matching the inline "Branch
 * from here" action. Bookmarks persist server-side by turn ordinal.
 *
 * Additive lab surface — mirrors the artifact-panel Sheet pattern in
 * lab-chat-view.tsx. Pure presentational: all state (turns, bookmarks) and all
 * effects (branch navigation, bookmark POST) live in the parent.
 */

import { useMemo, useState } from 'react';
import {
  ArrowUUpLeft,
  BookmarkSimple,
  ClockCounterClockwise,
  FunnelSimple,
  GitBranch,
  WarningCircle,
} from '@phosphor-icons/react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import type { HistoryTurn } from '@/lib/session-history';

export interface HistoryArtifactChip {
  key: string;
  messageId: string;
  toolName: string;
  label: string;
}

interface HistoryPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Turns in source (chronological) order — the panel renders newest first. */
  turns: HistoryTurn[];
  bookmarkedTurns: number[];
  artifacts: HistoryArtifactChip[];
  onToggleBookmark: (turnIndex: number, next: boolean) => void;
  /** Fork a non-destructive branch at this turn and navigate to it. */
  onRevert: (turn: HistoryTurn) => void;
  /** Undo the last edit = branch just before the most recent user turn. */
  onUndoLast: () => void;
  onOpenArtifact: (key: string) => void;
}

function formatTime(ts?: number): string | null {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

export function HistoryPanel({
  open,
  onOpenChange,
  turns,
  bookmarkedTurns,
  artifacts,
  onToggleBookmark,
  onRevert,
  onUndoLast,
  onOpenArtifact,
}: HistoryPanelProps) {
  const [bookmarksOnly, setBookmarksOnly] = useState(false);
  const bookmarkSet = useMemo(() => new Set(bookmarkedTurns), [bookmarkedTurns]);

  // Newest first. Filter to bookmarks when the toggle is on.
  const visible = useMemo(() => {
    const ordered = [...turns].reverse();
    return bookmarksOnly ? ordered.filter((t) => bookmarkSet.has(t.turnIndex)) : ordered;
  }, [turns, bookmarksOnly, bookmarkSet]);

  const artifactsByMessageId = useMemo(() => {
    const map = new Map<string, HistoryArtifactChip[]>();
    for (const chip of artifacts) {
      const list = map.get(chip.messageId) ?? [];
      list.push(chip);
      map.set(chip.messageId, list);
    }
    return map;
  }, [artifacts]);

  const lastTurn = turns[turns.length - 1];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-screen max-w-none p-0 sm:w-[440px] sm:max-w-md gap-0"
        showCloseButton
      >
        {/* Header */}
        <div className="border-b px-5 pt-5 pb-4">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise weight="duotone" className="size-5 text-muted-foreground" />
            <h2 className="text-base font-semibold">History</h2>
            <span className="text-xs text-muted-foreground">
              {turns.length} {turns.length === 1 ? 'turn' : 'turns'}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground/80">
            Revert opens a branch — your original chat is never changed.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBookmarksOnly((v) => !v)}
              aria-pressed={bookmarksOnly}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                bookmarksOnly
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-500'
                  : 'border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <FunnelSimple weight="bold" className="size-3.5" />
              Bookmarks only
            </button>
            {lastTurn && (
              <button
                type="button"
                onClick={onUndoLast}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowUUpLeft weight="bold" className="size-3.5" />
                Undo last edit
              </button>
            )}
          </div>
        </div>

        {/* Turn list */}
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
              <BookmarkSimple weight="duotone" className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {bookmarksOnly ? 'No bookmarked turns yet.' : 'No turns yet.'}
              </p>
            </div>
          ) : (
            <ol className="space-y-1.5">
              {visible.map((turn) => {
                const isBookmarked = bookmarkSet.has(turn.turnIndex);
                const time = formatTime(turn.timestamp);
                const chips = turn.messageIds.flatMap((mid) => artifactsByMessageId.get(mid) ?? []);
                return (
                  <li
                    key={turn.turnIndex}
                    className="group/turn rounded-xl border border-border/70 bg-muted/20 px-3 py-2.5 transition-colors hover:border-border hover:bg-muted/40"
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => onToggleBookmark(turn.turnIndex, !isBookmarked)}
                        aria-pressed={isBookmarked}
                        aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this turn'}
                        className={`mt-0.5 shrink-0 rounded-md p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
                          isBookmarked
                            ? 'text-amber-500'
                            : 'text-muted-foreground/50 hover:text-foreground'
                        }`}
                      >
                        <BookmarkSimple weight={isBookmarked ? 'fill' : 'bold'} className="size-4" />
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium uppercase tracking-[1.2px] text-muted-foreground/70">
                            Turn {turn.turnIndex + 1}
                          </span>
                          {time && (
                            <span className="text-[10px] text-muted-foreground/60">{time}</span>
                          )}
                          {turn.hasError && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                              <WarningCircle weight="fill" className="size-2.5" />
                              failed
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-sm font-medium text-foreground" title={turn.fullText || turn.title}>
                          {turn.title}
                        </p>
                        {turn.summary && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground" title={turn.summary}>
                            {turn.summary}
                          </p>
                        )}

                        {chips.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {chips.map((chip) => (
                              <button
                                key={chip.key}
                                type="button"
                                onClick={() => onOpenArtifact(chip.key)}
                                className="inline-flex max-w-full items-center gap-1 rounded-full border border-violet-500/25 bg-violet-500/[0.06] px-2 py-0.5 text-[11px] text-violet-300 transition-colors hover:border-violet-500/45 hover:bg-violet-500/[0.12] hover:text-violet-200"
                                title={chip.label}
                              >
                                <span className="truncate">{chip.label}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="mt-1.5 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/turn:opacity-100 focus-within:opacity-100">
                          <button
                            type="button"
                            onClick={() => onRevert(turn)}
                            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                          >
                            <GitBranch weight="bold" className="size-3" />
                            Revert to here
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
